import { cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import { DateTime } from 'luxon';
import React, { PropsWithChildren, ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Purchase } from 'apis/merchant-gateway/merchant-analytics/types';
import { CreditAcquisition } from 'apis/restaurant-api/merchant-admin/credits/types';
import { ReviewAttribution } from 'apis/restaurant-api/merchant-admin/purchases/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useRetailContext } from 'common/hooks';
import { Typography } from 'common/typography';
import SentOfferDetails from 'modules/insights-v2/components/ratings/CompensationOffers/SentOfferDetails';

import {
  DELIVERY_PROVIDER_TYPES,
  DELIVERY_STATUSES_V2,
  DELIVERY_STATUS_TAG_VARIANTS_V2,
  DELIVERY_TYPES_V2,
} from './constants';
import PurchaseMissingItem from './purchase-breakdown/PurchaseMissingItem';
import PurchaseBreakdownItem from './purchase-breakdown/PurchaseV2BreakdownItem';

type PurchaseDetailsContentProps = {
  purchase: Purchase;
  timezone: string;
  showFees: boolean;
  showTaxID: boolean;
  compensationOffer?: CreditAcquisition;
  compensationOfferBanner?: ReactNode;
};

const Title = styled(Typography.Heading6)``;

const Section = styled.div`
  margin-block-start: 8px;
  margin-block-end: 32px;
`;

const Attributions = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-block-start: 4px;
  margin-block-end: -8px;
`;

const Attribution = styled(Tag)`
  margin: 0 8px 8px 0;
`;

const DetailContainer = styled.div`
  margin-block-end: 16px;
`;
const Label = styled.div`
  ${typographyCss.Small}
  color: ${cssThemedValue('textSecondary')};
  margin-block-end: 4px;
`;
const Value = styled.div`
  ${typographyCss.Body3}
`;
const OfferFieldValue = styled(Value)`
  padding-block-start: 4px;
`;

const SummarySection = styled.div`
  margin-block-start: 24px;
`;
const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 12px 0;
`;
const TotalLabel = styled.div`
  ${typographyCss.Body3}
  font-weight: bold;
`;
const TotalValue = styled.div`
  ${typographyCss.Body3}
  font-weight: bold;
`;

type DetailProps = PropsWithChildren<{ label: ReactNode }>;
const Detail = ({ label, children }: DetailProps) => (
  <DetailContainer>
    <Label>{label}</Label>
    <Value>{children}</Value>
  </DetailContainer>
);
const Summary = ({ label, children }: DetailProps) => (
  <SummaryRow>
    <Value>{label}</Value>
    <Value>{children}</Value>
  </SummaryRow>
);
const SummaryTotal = ({ label, children }: DetailProps) => (
  <SummaryRow>
    <TotalLabel>{label}</TotalLabel>
    <TotalValue>{children}</TotalValue>
  </SummaryRow>
);

const PurchaseDetailsContent = ({
  purchase,
  timezone,
  showFees,
  showTaxID,
  compensationOffer,
  compensationOfferBanner,
}: PurchaseDetailsContentProps) => {
  const { locale } = useIntl();
  const isRetailContext = useRetailContext();

  const { orderNumber, timeReceived, timePreorder, basket, pricing, currency, review, consumer, delivery, status } =
    purchase;
  const deliveryTypeMessageId =
    (delivery.providerType && DELIVERY_PROVIDER_TYPES[delivery.providerType]) ?? DELIVERY_TYPES_V2[delivery.method];

  const fees = pricing.breakdown?.fees?.finalPrice?.gross;
  const totalBeforeDiscount = pricing.beforeDiscountPrice?.gross;
  const totalDiscountAmount = pricing.discount?.gross;
  const finalPrice = pricing.finalPrice?.gross;

  const reviewAttributionTranslations = useMemo<{ [key in ReviewAttribution]: string }>(
    () => ({
      appearance: 'purchases.attributions.appearance',
      atmosphere: 'purchases.attributions.atmosphere',
      'cold food': 'purchases.attributions.coldFood',
      comment: 'purchases.attributions.comment',
      courier: 'purchases.attributions.courier',
      'delivery instructions': 'purchases.attributions.deliveryInstructions',
      'delivery time': 'purchases.attributions.deliveryTime',
      estimate: 'purchases.attributions.estimate',
      experience: 'purchases.attributions.experience',
      flavour: 'purchases.attributions.flavour',
      food: 'purchases.attributions.food',
      'had to queue': 'purchases.attributions.hadToQueue',
      'missing item': 'purchases.attributions.missingItem',
      packaging: 'purchases.attributions.packaging',
      'portion size': 'purchases.attributions.portionSize',
      'product damaged': 'purchases.attributions.productDamaged',
      quality: 'purchases.attributions.quality',
      selection: 'purchases.attributions.selection',
      staff: 'purchases.attributions.staff',
      texture: 'purchases.attributions.texture',
      'value for money': 'purchases.attributions.valueForMoney',
      venue: isRetailContext ? 'purchases.attributions.venue-retail' : 'purchases.attributions.venue',
    }),
    [isRetailContext],
  );

  return (
    <>
      <Title>
        <FormattedMessage id="purchases.order-information" />
      </Title>

      <Section>
        {showTaxID && <Detail label="TaxID (NIP)">{consumer.taxId || '-'}</Detail>}

        <Detail label={<FormattedMessage id="purchases.headings.order-number" />}>#{orderNumber}</Detail>

        <Detail label={<FormattedMessage id="purchases.headings.customer" />}>{consumer.name}</Detail>

        <Detail label={<FormattedMessage id="purchases.delivery-type" />}>
          {deliveryTypeMessageId ? <FormattedMessage id={deliveryTypeMessageId} /> : delivery.method}
        </Detail>

        <Detail label={<FormattedMessage id="purchases.status" />}>
          <Tag variant={DELIVERY_STATUS_TAG_VARIANTS_V2[status] ?? 'lightBlack'}>
            {status in DELIVERY_STATUSES_V2 ? <FormattedMessage id={DELIVERY_STATUSES_V2[status]} /> : status}
          </Tag>
        </Detail>

        {!!timePreorder && (
          <Detail label={<FormattedMessage id="purchases.preorder-time" />}>
            {DateTime.fromISO(timePreorder, { zone: timezone }).toLocaleString(DateTime.DATETIME_SHORT, { locale })}
          </Detail>
        )}

        <Detail label={<FormattedMessage id="purchases.order-placed" />}>
          {DateTime.fromISO(timeReceived, { zone: timezone }).toLocaleString(DateTime.DATETIME_SHORT, { locale })}
        </Detail>

        {!!consumer.comment && (
          <Detail label={<FormattedMessage id="purchases.headings.order-comment" />}>{consumer.comment}</Detail>
        )}

        {!!review && (
          <>
            {!!review.rating && (
              <Detail label={<FormattedMessage id="purchases.review-score" />}>{review.rating} / 5</Detail>
            )}
            {!!review.attributions?.length && (
              <Detail
                label={
                  typeof review.rating === 'number' ? (
                    review.rating < 5 ? (
                      <FormattedMessage id="purchases.headings.attributions-bad" />
                    ) : (
                      <FormattedMessage id="purchases.headings.attributions-good" />
                    )
                  ) : (
                    <FormattedMessage id="purchases.headings.attributions" />
                  )
                }
              >
                <Attributions>
                  {review.attributions.map(attribution => (
                    <Attribution key={attribution} variant="blue">
                      {attribution in reviewAttributionTranslations ? (
                        <FormattedMessage
                          id={reviewAttributionTranslations[attribution.toLowerCase() as ReviewAttribution]}
                        />
                      ) : (
                        attribution
                      )}
                    </Attribution>
                  ))}
                </Attributions>
              </Detail>
            )}
            {!!review.comment && (
              <Detail label={<FormattedMessage id="purchases.review-comment" />}>{review.comment}</Detail>
            )}
            {compensationOffer ? (
              <Detail label={<FormattedMessage id="purchases.headings.review-compensation-offer" />}>
                <OfferFieldValue>
                  <SentOfferDetails timezone={timezone} sentOffer={compensationOffer} />
                </OfferFieldValue>
              </Detail>
            ) : (
              compensationOfferBanner
            )}
          </>
        )}
      </Section>

      {!!basket.missingItems.length && (
        <>
          <Title>
            <FormattedMessage id="purchases.headings.missing-order-items-details" />{' '}
          </Title>

          <Section>
            {basket.missingItems.map((missingItem, index) => (
              <PurchaseMissingItem
                key={`${missingItem.itemId}-${index}`}
                missingItem={missingItem}
                currency={currency}
              />
            ))}
          </Section>
        </>
      )}

      <Title>
        <FormattedMessage id="purchases.headings.order-details" />
      </Title>

      <Section>
        {basket.items.map((item, index) => (
          <PurchaseBreakdownItem
            key={`item-${item.itemId}-${index}`}
            item={item}
            currency={currency}
            substitution={basket.substitutions?.[item.rowNumber]}
          />
        ))}

        <SummarySection>
          {showFees && !!fees && (
            <Summary label={<FormattedMessage id="purchase-history.fees" />}>
              <MonetaryAmount amount={fees} currency={currency} />
            </Summary>
          )}

          <SummaryTotal label={<FormattedMessage id="purchases.labels.order-total" values={{ currency }} />}>
            <MonetaryAmount amount={totalBeforeDiscount ?? 0} currency={currency} />
          </SummaryTotal>
        </SummarySection>

        <SummarySection>
          {!!totalDiscountAmount && (
            <Summary label={<FormattedMessage id="purchases.discount.sponsored" />}>
              <MonetaryAmount amount={totalDiscountAmount} currency={currency} />
            </Summary>
          )}

          {!!totalDiscountAmount && !!finalPrice && (
            <SummaryTotal
              label={<FormattedMessage id="purchases.labels.order-total-after-deductions" values={{ currency }} />}
            >
              <MonetaryAmount amount={finalPrice} currency={currency} />
            </SummaryTotal>
          )}
        </SummarySection>
      </Section>
    </>
  );
};

export default PurchaseDetailsContent;
